import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Icon, Tooltip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import cx from 'classnames';
import { NotificationManager } from 'Components/Notifications';
import { defaultProductName } from 'Core/defaultValues';
import AppIcon from '../appIcon';
import { getProductName, keyAccepted } from '../../constants';
// Actions
import { accountAppsCheckApiKey, accountAppsUpdate, openAppsEdit } from '../../redux/slice';
import './styles.scss';

// Statuses the customer can set; Inactive pauses the vehicle sync and the transmission to the app
const STATUSES = ['Active', 'Inactive'];
const RADIO_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

/**
 * Edit side panel of an integration (design "Edit integration", proposal B). The name is fixed. The API key field
 * shows the masked key and clears on focus: a new key must be verified with checkCompanyIntegrationApiKey before
 * saving, and leaving the field empty keeps the current key. The status is the switch of the vehicle sync and the
 * transmission. Save sends only what changed to updateCompanyIntegration. The grid remounts the panel when the
 * silent reload brings a newer row, so the values here are always the row's.
 */
const EditIntegration = ({ intl: { messages }, integration }) => {
    const dispatch = useDispatch();
    const { saving } = useSelector((state) => state.accountAppsRedux.edit);
    const reseller = useSelector((state) => state.resellerRedux?.current_reseller);
    const productName = getProductName(reseller, defaultProductName);
    const verifyRun = useRef(0);
    const radioRefs = useRef({});

    const masked = integration.apiKeyMasked || '';
    const [key, setKey] = useState(masked);
    const [keyShow, setKeyShow] = useState(false);
    // The user typed in the key field (an empty field is then an error until it is left)
    const [dirty, setDirty] = useState(false);
    // Inline "Verify" of a new key: idle | checking | ok | fail | unreachable, for the key in kvKey
    const [kv, setKv] = useState('idle');
    const [kvKey, setKvKey] = useState('');
    const [check, setCheck] = useState(null);
    const [status, setStatus] = useState(integration.status);

    // A check that answers after the panel closed is ignored
    useEffect(() => () => { verifyRun.current += 1; }, []);

    const appName = integration.integrationName;
    const keyMax = integration.apiKeyMaxLength || null;
    const tr = (textKey, values = {}) => Object.entries({ _APP_: appName, _PRODUCT_: productName, ...values })
        .reduce((text, [placeholder, value]) => text.split(placeholder).join(value), messages[textKey] || '');

    const keyTrim = key.trim();
    const isMask = key === masked;
    const empty = keyTrim === '';
    const newKey = !empty && !isMask;
    const kvOk = newKey && kv === 'ok' && kvKey === keyTrim;
    const kvFail = newKey && (kv === 'fail' || kv === 'unreachable') && kvKey === keyTrim;
    const kvStale = newKey && (kv === 'ok' || kv === 'fail') && !kvOk && !kvFail;
    const keyErr = empty && dirty;
    const statusChanged = status !== integration.status;
    const canSave = (kvOk || statusChanged) && !empty && (!newKey || kvOk) && !saving;
    const keyCountClass = keyMax && key.length >= keyMax ? 'is-max' : keyMax && key.length >= keyMax * 0.9 ? 'is-near' : null;
    const keyDescribedBy = keyErr ? 'account-apps-edit-key-error' : (kvOk || kvFail || kvStale) ? 'account-apps-edit-key-status' : undefined;
    const title = tr('accountAppsEdit_title');

    const close = () => {
        if (!saving) dispatch(openAppsEdit(null));
    };

    // The mask stands for the current key: it clears to paste a new one and comes back when the field is left empty
    const onKeyFocus = () => {
        if (isMask) setKey('');
    };
    const onKeyBlur = () => {
        if (keyTrim !== '') return;
        setKey(masked);
        setDirty(false);
        setKv('idle');
        setKvKey('');
        setCheck(null);
    };
    const onKeyChange = (e) => {
        setKey(keyMax ? e.target.value.slice(0, keyMax) : e.target.value);
        setDirty(true);
    };

    const verifyKey = async () => {
        if (!newKey || kv === 'checking' || kvOk) return;
        const value = keyTrim;
        verifyRun.current += 1;
        const run = verifyRun.current;
        setKv('checking');
        setKvKey(value);
        setCheck(null);
        try {
            const result = await dispatch(accountAppsCheckApiKey({ integrationCode: integration.integrationCode, apiKey: value })).unwrap();
            if (run === verifyRun.current) {
                setCheck(result);
                setKv(keyAccepted(result) ? 'ok' : 'fail');
            }
        } catch (e) {
            if (run === verifyRun.current) setKv('unreachable');
        }
    };

    const save = async () => {
        if (!canSave) return;
        const changes = { id: integration.id };
        if (kvOk) changes.apiKey = keyTrim;
        if (statusChanged) changes.status = status;
        try {
            await dispatch(accountAppsUpdate(changes)).unwrap();
            NotificationManager.success(messages['accountApps_updated'], messages['success'], 3500, null, null, 'success');
        } catch (e) {
            NotificationManager.error(e?.error || messages['errorMessage'], messages['error'], 5000, null, null, 'warning');
        }
    };

    // Enter saves when everything is ready, otherwise it verifies the new key
    const onKeyEnter = (e) => {
        if (e.key !== 'Enter') return;
        if (canSave) save();
        else if (newKey && !kvOk) verifyKey();
    };

    // The arrows move between the two statuses (ARIA radio group: one tab stop, the checked option)
    const onRadioKeyDown = (e) => {
        if (!RADIO_KEYS.includes(e.key)) return;
        e.preventDefault();
        const next = status === 'Active' ? 'Inactive' : 'Active';
        setStatus(next);
        radioRefs.current[next]?.focus();
    };

    const keyStatusText = () => {
        if (kvOk) return tr(check?.accountName ? 'accountAppsEdit_keyAcceptedAccount' : 'accountAppsEdit_keyAccepted', { _ACCOUNT_: check?.accountName || '' });
        if (kvFail) return tr(kv === 'unreachable' ? 'accountAppsWizard_keyUnreachable' : check?.authenticated ? 'accountAppsWizard_keyNoVehicles' : 'accountAppsWizard_keyRejected');
        return messages['accountAppsWizard_keyStale'];
    };

    return (
        <Drawer
            anchor="right"
            open
            onClose={close}
            className="account-apps-edit"
            PaperProps={{ className: 'account-apps-edit-paper', role: 'dialog', 'aria-label': title }}
        >
            <header className="edit-header">
                <span className="edit-title">{title}</span>
                <Tooltip title={messages['accountAppsEdit_close']} placement="bottom">
                    <span>
                        <button type="button" className="edit-icon-button edit-close" aria-label={messages['accountAppsEdit_close']} disabled={saving} onClick={close}>
                            <Icon>close</Icon>
                        </button>
                    </span>
                </Tooltip>
            </header>

            <div className="edit-body">
                <div className="edit-label">{messages['accountAppsEdit_name']}</div>
                <div className="edit-name">
                    <AppIcon className="edit-name-icon" icon={integration.icon} color={integration.color} name={appName} />
                    <span>{appName}</span>
                </div>

                <div className="edit-label edit-label-key"><span>*</span>{messages['accountApps_apiKey']}</div>
                <div className="edit-key-row">
                    <div className="edit-key-field">
                        <input
                            type={isMask || keyShow ? 'text' : 'password'}
                            value={key}
                            onChange={onKeyChange}
                            onFocus={onKeyFocus}
                            onBlur={onKeyBlur}
                            onKeyDown={onKeyEnter}
                            maxLength={keyMax || undefined}
                            placeholder={messages['accountAppsWizard_keyPlaceholder']}
                            aria-label={messages['accountApps_apiKey']}
                            aria-invalid={keyErr || kvFail || undefined}
                            aria-describedby={keyDescribedBy}
                            autoComplete="off"
                            spellCheck={false}
                            disabled={kv === 'checking' || saving}
                            className={cx({ 'is-error': keyErr || kvFail, 'is-ok': kvOk })}
                        />
                        {
                            !isMask &&
                            <Tooltip title={keyShow ? messages['accountAppsWizard_hideKey'] : messages['accountAppsWizard_showKey']} placement="top">
                                <button
                                    type="button"
                                    className="edit-icon-button edit-eye"
                                    aria-label={keyShow ? messages['accountAppsWizard_hideKey'] : messages['accountAppsWizard_showKey']}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setKeyShow((show) => !show)}
                                >
                                    {keyShow ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                </button>
                            </Tooltip>
                        }
                    </div>
                    <button
                        type="button"
                        className={cx('edit-verify', { 'is-empty': !newKey })}
                        onClick={verifyKey}
                        disabled={!newKey || kv === 'checking' || kvOk}
                    >
                        {(kv === 'checking' || kvOk) && <Icon className={cx('edit-verify-icon', { 'edit-spin': kv === 'checking' })}>{kv === 'checking' ? 'autorenew' : 'check'}</Icon>}
                        {kv === 'checking' ? messages['accountAppsWizard_verifying'] : kvOk ? messages['accountAppsWizard_verified'] : messages['accountAppsWizard_verify']}
                    </button>
                </div>
                {keyErr && <div id="account-apps-edit-key-error" role="alert" className="edit-key-error">{messages['accountAppsWizard_keyRequired']}</div>}
                {
                    (kvOk || kvFail || kvStale) &&
                    <div id="account-apps-edit-key-status" role="status" className={cx('edit-key-status', { 'is-ok': kvOk, 'is-fail': kvFail, 'is-stale': kvStale })}>
                        <Icon className="edit-key-status-icon">{kvOk ? 'check_circle' : kvFail ? 'error' : 'warning'}</Icon>
                        <span>{keyStatusText()}</span>
                    </div>
                }
                <div className="edit-key-hint">
                    <span>{messages['accountAppsEdit_keyHint']}</span>
                    {keyMax && !isMask && <span className={cx('edit-key-count', keyCountClass)}>{key.length} / {keyMax}</span>}
                </div>

                <div className="edit-label edit-label-status">{messages['accountAppsEdit_status']}</div>
                <div role="radiogroup" aria-label={messages['accountAppsEdit_status']} className="edit-status">
                    {STATUSES.map((value) => (
                        <button
                            key={value}
                            ref={(node) => { radioRefs.current[value] = node; }}
                            type="button"
                            role="radio"
                            aria-checked={status === value}
                            tabIndex={status === value || !STATUSES.includes(status) ? 0 : -1}
                            className={cx('edit-status-option', `is-${value.toLowerCase()}`, { 'is-selected': status === value })}
                            disabled={saving}
                            onClick={() => setStatus(value)}
                            onKeyDown={onRadioKeyDown}
                        >
                            <span className="edit-status-dot" />
                            {messages[value === 'Active' ? 'accountApps_active' : 'accountApps_inactive']}
                        </button>
                    ))}
                </div>
                {
                    integration.syncsVehicles &&
                    <div className="edit-info">
                        <InfoOutlinedIcon />
                        <div>{tr('accountAppsEdit_info')}</div>
                    </div>
                }
            </div>

            <footer className="edit-footer">
                <button type="button" className="edit-text-button" disabled={saving} onClick={close}>{messages['accountAppsEdit_cancel']}</button>
                <button type="button" className="edit-primary" disabled={!canSave} onClick={save}>
                    {saving ? messages['accountAppsEdit_saving'] : messages['accountAppsEdit_save']}
                </button>
            </footer>
        </Drawer>
    );
};

export default injectIntl(EditIntegration);
